import React from 'react';

export interface CitationMarkerData {
  docName: string;
  docId?: string;
  page: string | number;
  confidence?: number;
}

interface CitationMarkerProps {
  citation: CitationMarkerData;
  onClick: () => void;
  /** Optional letter prefix shown inside the badge (A, B, C…) when a report
   *  has multiple distinct source documents. Disambiguates at a glance. */
  sourceLetter?: string;
  className?: string;
}

/**
 * Small sentence-end citation marker. Renders as an inline page icon (~16px)
 * with the document name + page revealed on hover. Click triggers the
 * citation viewer.
 *
 * Replaces the old wide blue "Filename.pdf - p.3" pills that broke paragraph
 * flow when many citations appeared in one section.
 */
export const CitationMarker: React.FC<CitationMarkerProps> = ({
  citation,
  onClick,
  sourceLetter,
  className = '',
}) => {
  const tooltip = `${citation.docName} · Page ${citation.page}${
    citation.confidence ? ` · ${Math.round(citation.confidence * 100)}% match` : ''
  }`;

  return (
    <button
      type="button"
      onClick={onClick}
      title={tooltip}
      aria-label={tooltip}
      className={`inline-flex items-center justify-center align-baseline
        w-5 h-5 ml-0.5 rounded
        text-blue-600 dark:text-blue-300
        bg-blue-50 dark:bg-blue-900/30
        hover:bg-blue-100 dark:hover:bg-blue-900/60
        hover:text-blue-700 dark:hover:text-blue-200
        border border-blue-200 dark:border-blue-800
        transition-colors
        focus:outline-none focus:ring-2 focus:ring-blue-400
        ${className}`}
    >
      {sourceLetter ? (
        <span className="text-[10px] font-semibold leading-none">{sourceLetter}</span>
      ) : (
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      )}
    </button>
  );
};

export default CitationMarker;
